/**
 * URL helpers for the borrower and investor portals (GLASS-490 / GLASS-508).
 *
 * Every page that sends a borrower or investor to "their login" goes through
 * `portalLoginPath`, so the lender's link name lands in the URL whenever it is
 * known and the bare `/…/login` is used only when it is not.
 */

export type PortalRole = 'borrower' | 'investor'

export function portalLoginPath(role: PortalRole, slug?: string | null): string {
  return slug ? `/${role}/login/${slug}` : `/${role}/login`
}

export function portalHomePath(role: PortalRole): string {
  return `/${role}`
}

/**
 * The forgot-password page for a portal, carrying the lender the login page
 * was showing (if any) so the next page draws the same brand. The brand is
 * passed explicitly rather than guessed from the cookie: a visitor on an
 * unknown link sees Glass, and must keep seeing Glass on the next page.
 */
export function forgotPasswordPath(role: PortalRole, slug?: string | null): string {
  return withSearchParams('/forgot-password', { from: role, brand: slug })
}

/**
 * Append `redirectTo` (and any other params) to a login path, preserving
 * whatever was already in the query string.
 */
export function withSearchParams(
  path: string,
  params: Record<string, string | null | undefined>
): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value) searchParams.set(key, value)
  }
  const query = searchParams.toString()
  return query ? `${path}?${query}` : path
}

/** Stand-in origin for resolving a redirect target; never requested. */
const REDIRECT_RESOLUTION_ORIGIN = 'http://same-origin.invalid'

/**
 * Only same-origin absolute paths are honoured as a post-login destination, so
 * a crafted `?redirectTo=` can't turn a login page into an open redirect.
 * Everything else falls back to the portal home.
 *
 * The value is resolved the way a browser resolves a `Location` header, not
 * just checked for a leading slash: browsers read a backslash as a slash and
 * drop tabs and newlines, so `/\evil.example` and `/<tab>/evil.example` both
 * leave the site (GLASS-549).
 */
export function safePortalRedirect(
  value: string | null | undefined,
  fallback: string
): string {
  if (!value || !value.startsWith('/')) return fallback
  try {
    const resolvedUrl = new URL(value, REDIRECT_RESOLUTION_ORIGIN)
    return resolvedUrl.origin === REDIRECT_RESOLUTION_ORIGIN ? value : fallback
  } catch {
    return fallback
  }
}

/**
 * The login link shown once a password is set, carrying where to go after
 * signing in (GLASS-452: an Apply Now applicant lands on their new loan).
 * Kept only for a page in the same portal as that login, so a borrower link
 * never sends an admin or investor login somewhere it can't open.
 */
export function withPostLoginRedirect(loginUrl: string, redirectTo: string | null | undefined): string {
  const role = (['borrower', 'investor'] as const).find((portalRole) =>
    loginUrl.startsWith(`/${portalRole}/login`)
  )
  if (!role) return loginUrl
  const safeRedirectTo = safePortalRedirect(redirectTo, '')
  if (!safeRedirectTo.startsWith(`/${role}/`)) return loginUrl
  return withSearchParams(loginUrl, { redirectTo: safeRedirectTo })
}

/**
 * The portal a session belongs to, from the `userType` stored on the Session
 * row. Admin and support sessions have no portal.
 */
export function portalRoleFromUserType(
  userType: string | null | undefined
): PortalRole | null {
  return userType === 'borrower' || userType === 'investor' ? userType : null
}
